import { useEffect, useMemo, useState } from 'react'
import { useApiFetch } from '@/hooks/useApiFetch'
import { useToast } from '@/components/ToastContainer'
import { apiFetch } from '@/lib/api'
import { Button, Textarea, Spinner, Alert } from '@/ui'
import styles from './ProfanitySettings.module.css'

type Patterns = {
  korean: string[]
  english: string[]
  hangulAbbreviations: string[]
  standaloneNumeric: string[]
}

interface SettingRow {
  key: string
  value: Patterns
  category: string
  label: string
  description: string | null
}

type FieldKey = keyof Patterns

const FIELD_LABELS: Record<FieldKey, { label: string; help: string }> = {
  korean: {
    label: '한국어 단어',
    help: '완전한 한국어 욕설 단어. 한 줄에 하나씩. 예: 씨발, 병신',
  },
  english: {
    label: '영어 단어',
    help: '영어 욕설. 소문자 권장. 반복문자(fuuck)·leet(sh1t, f*ck) 자동 감지. 예: fuck, bitch',
  },
  hangulAbbreviations: {
    label: '한글 자모 축약',
    help: '자모 단위 축약 표기. 예: ㅅㅂ, ㅂㅅ',
  },
  standaloneNumeric: {
    label: '단독 숫자 욕설',
    help: '텍스트 전체가 이 숫자일 때만 차단 (문맥은 통과). 예: 18, 19',
  },
}

const FIELD_KEYS: FieldKey[] = ['korean', 'english', 'hangulAbbreviations', 'standaloneNumeric']

function toTextarea(arr: string[] | undefined): string {
  return (arr ?? []).join('\n')
}

function fromTextarea(s: string): string[] {
  return s
    .split('\n')
    .map((w) => w.trim())
    .filter(Boolean)
}

export function ProfanitySettings() {
  const { data, isLoading, refetch } = useApiFetch<SettingRow[]>('/api/admin/settings?category=moderation')
  const row = useMemo(() => (data ?? []).find((r) => r.key === 'profanity_words') ?? null, [data])

  const [draft, setDraft] = useState<Record<FieldKey, string>>({
    korean: '',
    english: '',
    hangulAbbreviations: '',
    standaloneNumeric: '',
  })
  const [saving, setSaving] = useState(false)
  const { addToast } = useToast()

  useEffect(() => {
    if (!row) return
    setDraft({
      korean: toTextarea(row.value.korean),
      english: toTextarea(row.value.english),
      hangulAbbreviations: toTextarea(row.value.hangulAbbreviations),
      standaloneNumeric: toTextarea(row.value.standaloneNumeric),
    })
  }, [row])

  const handleSave = async () => {
    setSaving(true)
    try {
      const body = {
        key: 'profanity_words',
        value: {
          korean: fromTextarea(draft.korean),
          english: fromTextarea(draft.english),
          hangulAbbreviations: fromTextarea(draft.hangulAbbreviations),
          standaloneNumeric: fromTextarea(draft.standaloneNumeric),
        },
        category: 'moderation',
        label: '욕설 필터 단어 사전',
        description: '방명록/RSVP/청첩장 편집 시 차단되는 단어 목록',
      }
      await apiFetch('/api/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify(body),
      })
      addToast({ type: 'success', message: '저장 완료' })
      refetch()
    } catch (err: any) {
      addToast({ type: 'error', message: err.message || '저장 실패' })
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>욕설 필터 단어 사전</h1>
          <p className={styles.subtitle}>
            방명록·RSVP·청첩장 편집 저장 시 차단되는 단어 목록입니다.
          </p>
        </div>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </Button>
      </div>

      <Alert variant="info" title="적용 시점">
        저장 즉시 서버 인스턴스의 캐시가 초기화됩니다. 다중 인스턴스 환경에서는 최대 5분까지 분산 반영 지연이 있을 수 있습니다.
      </Alert>

      {!row && (
        <Alert variant="warning" title="DB row 미존재">
          app_settings에 'profanity_words' row가 없어 기본값이 사용 중입니다. 저장하면 DB에 생성됩니다.
        </Alert>
      )}

      <div className={styles.fields}>
        {FIELD_KEYS.map((k) => (
          <div key={k} className={styles.field}>
            <label className={styles.label}>{FIELD_LABELS[k].label}</label>
            <p className={styles.help}>{FIELD_LABELS[k].help}</p>
            <Textarea
              rows={10}
              value={draft[k]}
              onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
              placeholder="한 줄에 한 단어"
            />
          </div>
        ))}
      </div>
    </div>
  )
}
